import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { OidcSignupCompleteRequest } from '@ridango/core';
import Env from '@ridango/core/env';
import { CustomerRegistrationCompleteRequest, CustomerService } from '../../../customer/customer.service';
import * as OidcSignupActions from './oidc-signup.actions';
import { PhoneNumberUtil } from 'google-libphonenumber';

@Injectable({
    providedIn: 'root'
})
export class OidcSignupEffects {
    constructor(
        @Inject('ENV') private readonly env: Env,
        private readonly actions$: Actions,
        private readonly customerService: CustomerService,
        private readonly translate: TranslateService,
        private readonly router: Router
    ) {}

    completeSignup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OidcSignupActions.completeSignup),
            switchMap(({ payload }: { payload: OidcSignupCompleteRequest }) => {
                const registrationPayload = this.createCompleteRegistrationPayload(payload);

                return this.customerService.completeCustomerRegistration(registrationPayload).pipe(
                    tap(() => {
                        this.router.navigate([this.translate.currentLang, 'explore']);
                    }),
                    map(() => OidcSignupActions.completeSignupSuccess()),
                    catchError((error) => of(OidcSignupActions.completeSignupFailed({ payload: error })))
                );
            })
        );
    });

    private createCompleteRegistrationPayload(payload: OidcSignupCompleteRequest): CustomerRegistrationCompleteRequest {
        const parsedPhone = this.parsePhone(payload.phoneNumber);
        return {
            personalCode: payload.personalCode,
            phone: parsedPhone
        };
    }

    private parsePhone(phoneNumber: string): { code: string; number: string } {
        const trimmedPhoneNumber = (phoneNumber ?? '').trim();

        try {
            const phoneUtil = PhoneNumberUtil.getInstance();
            const parsed = phoneUtil.parseAndKeepRawInput(trimmedPhoneNumber);
            const countryCode = parsed.getCountryCode();
            const nationalNumber = parsed.getNationalNumber();

            if (countryCode && nationalNumber) {
                return {
                    code: `+${countryCode}`,
                    number: `${nationalNumber}`
                };
            }
        } catch (error) {
            console.error('[OidcSignup] Failed to parse phone number:', trimmedPhoneNumber, error);
        }

        return {
            code: this.env.DEFAULT_PHONE_COUNTRY_CODE ?? '',
            number: trimmedPhoneNumber.replace(/^\+\d{1,4}/, '').replace(/\s+/g, '')
        };
    }
}
